import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PortalShell } from "@/pages/portal/PortalShell";
import { LANGUAGES, STEP_I18N, FIELD_I18N, FIELD_EXAMPLES } from "@/lib/wizardTranslations";
import { VoiceInput } from "@/components/portal/VoiceInput";
import { ArrowLeft, ArrowRight, Save, Sparkles } from "lucide-react";

const STEPS = [
  {
    id: "founder", title: "Founder", fields: [
      { key: "founder_status", label: "Founder status", type: "select", options: ["Founder", "Co-Founder", "Founding Team"] },
      { key: "entity_type", label: "Existing company or individual?", type: "select", options: ["Individual", "Existing Company"] },
      { key: "team_size", label: "Team size", type: "select", options: ["Just me", "2-3", "4-10", "10+"] },
      { key: "cofounders", label: "Co-founder details (optional)", type: "textarea" },
    ],
  },
  { id: "problem", title: "Problem", fields: [{ key: "problem", label: "What problem are you solving?", type: "textarea", required: true }] },
  {
    id: "solution", title: "Solution", fields: [
      { key: "solution", label: "What is your proposed solution?", type: "textarea", required: true },
      { key: "how_customers_use", label: "How will customers use your product/service?", type: "textarea" },
      { key: "differentiation", label: "What makes your idea different?", type: "textarea" },
    ],
  },
  {
    id: "customer", title: "Customer", fields: [
      { key: "target_customer", label: "Who is your customer?", type: "text" },
      { key: "customer_age", label: "Customer age range", type: "text" },
      { key: "customer_location", label: "Customer location", type: "text" },
      { key: "customer_type", label: "B2B / B2C / B2B2C", type: "select", options: ["B2C", "B2B", "B2B2C"] },
      { key: "expected_customers", label: "Expected monthly customers", type: "text" },
    ],
  },
  {
    id: "market", title: "Market", fields: [
      { key: "industry", label: "Industry", type: "text", required: true },
      { key: "business_category", label: "Business category", type: "text" },
      { key: "business_location", label: "Business location", type: "text" },
      { key: "target_geography", label: "Target geography", type: "text" },
      { key: "target_countries", label: "Target countries", type: "text" },
      { key: "current_stage", label: "Current stage", type: "select", options: ["Idea", "Research", "Prototype", "MVP", "Product Ready", "Revenue Generating", "Scaling"] },
      { key: "launch_date", label: "Expected launch date", type: "text" },
    ],
  },
  { id: "competition", title: "Competition", fields: [{ key: "competitors", label: "Who are your competitors?", type: "textarea" }] },
  {
    id: "revenue", title: "Revenue", fields: [
      { key: "revenue_models", label: "Revenue model(s) — select all that apply", type: "multi", options: ["Product Sales", "Subscription", "Commission", "Marketplace", "SaaS", "Service Fee", "Advertising", "Licensing", "Franchise", "Other"] },
      { key: "how_make_money", label: "How will you make money?", type: "textarea" },
      { key: "expected_price", label: "Expected selling price / average order value (₹)", type: "text" },
      { key: "expected_monthly_sales", label: "Expected monthly sales (units/orders)", type: "text" },
    ],
  },
  {
    id: "operations", title: "Operations", fields: [
      { key: "manufacturing", label: "Manufacturing required?", type: "yesno" },
      { key: "machinery", label: "Machinery required?", type: "yesno" },
      { key: "warehouse", label: "Warehouse required?", type: "yesno" },
      { key: "employees", label: "Employees required?", type: "yesno" },
      { key: "office", label: "Office required?", type: "yesno" },
      { key: "import_export", label: "Import / Export involved?", type: "yesno" },
    ],
  },
  {
    id: "investment", title: "Investment", fields: [
      { key: "current_investment", label: "Current investment so far (₹)", type: "text" },
      { key: "planned_investment", label: "Planned investment (₹)", type: "text" },
      { key: "funding_requirement", label: "External funding requirement", type: "text" },
    ],
  },
  { id: "team", title: "Team", fields: [{ key: "key_roles", label: "Key roles you will need", type: "textarea" }] },
  {
    id: "technology", title: "Technology", fields: [
      { key: "website", label: "Website required?", type: "yesno" },
      { key: "mobile_app", label: "Mobile app required?", type: "yesno" },
      { key: "software", label: "Custom software required?", type: "yesno" },
    ],
  },
  {
    id: "registrations", title: "Registrations", fields: [
      { key: "existing_registrations", label: "Existing registrations (if any)", type: "text" },
      { key: "licenses_aware", label: "Do you know which licenses you need?", type: "select", options: ["Yes", "No", "Not sure"] },
    ],
  },
  { id: "documents", title: "Documents", info: "No uploads needed right now — after submission, your secure Document Vault opens and specialists will request exactly what they need.", fields: [] },
  { id: "review", title: "Review" },
];

function StepField({ field, value, onChange, lang }) {
  const id = `wizard-${field.key}`;
  const mic = (
    <VoiceInput
      lang={lang}
      testid={`${id}-voice`}
      onResult={(t) => onChange(((value || "") + (value ? " " : "") + t).trim())}
    />
  );
  if (field.type === "textarea") {
    return (
      <div className="relative">
        <Textarea id={id} data-testid={id} rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-[#06080E] border-white/10 pr-12" />
        {mic}
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger id={id} data-testid={id} className="bg-[#06080E] border-white/10 text-slate-200"><SelectValue placeholder="Select..." /></SelectTrigger>
        <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200">
          {field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "yesno") {
    return (
      <div className="flex gap-2.5" data-testid={id}>
        {["yes", "no"].map((opt) => (
          <button
            type="button"
            key={opt}
            data-testid={`${id}-${opt}`}
            onClick={() => onChange(opt)}
            className={`px-6 py-2.5 rounded-full text-sm font-medium transition-colors ${
              value === opt ? "bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E]" : "glass text-slate-300 hover:text-white"
            }`}
          >
            {opt === "yes" ? "Yes" : "No"}
          </button>
        ))}
      </div>
    );
  }
  if (field.type === "multi") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-wrap gap-2.5" data-testid={id}>
        {field.options.map((opt) => (
          <button
            type="button"
            key={opt}
            data-testid={`${id}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => onChange(arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt])}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              arr.includes(opt) ? "bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E]" : "glass text-slate-300 hover:text-white"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    );
  }
  return (
    <div className="relative">
      <Input id={id} data-testid={id} value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-[#06080E] border-white/10 pr-12" />
      {mic}
    </div>
  );
}

export default function StartupWizard() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [startupId, setStartupId] = useState(id || null);
  const [name, setName] = useState("");
  const [brandName, setBrandName] = useState("");
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lang, setLang] = useState("en");

  useEffect(() => {
    api.get("/portal/auth/me").then((r) => {
      if (r.data.role !== "founder") { navigate("/portal"); return; }
      setUser(r.data);
    }).catch(() => navigate("/portal"));
  }, [navigate]);

  useEffect(() => {
    if (id) {
      api.get(`/portal/startups/${id}`).then((r) => {
        setName(r.data.name);
        setBrandName(r.data.brand_name || "");
        setAnswers(r.data.answers || {});
        setLang(r.data.answers?._language || "en");
      }).catch(() => navigate("/dashboard"));
    }
  }, [id, navigate]);

  const current = STEPS[step];

  const save = async (silent = false) => {
    setSaving(true);
    try {
      let sid = startupId;
      if (!sid) {
        if (!name.trim()) {
          toast.error("Give your startup project a name first");
          setSaving(false);
          return null;
        }
        const res = await api.post("/portal/startups", { name, brand_name: brandName });
        sid = res.data.id;
        setStartupId(sid);
      }
      await api.put(`/portal/startups/${sid}`, { answers: { ...answers, _language: lang }, name, brand_name: brandName });
      if (!silent) toast.success("Progress saved");
      return sid;
    } catch (err) {
      toast.error(formatApiError(err));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const next = async () => {
    if (step === 0 && !startupId) {
      const sid = await save(true);
      if (!sid) return;
    } else if (startupId) {
      await api.put(`/portal/startups/${startupId}`, { answers: { ...answers, _language: lang }, name, brand_name: brandName }).catch(() => {});
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const sid = await save(true);
      if (!sid) return;
      await api.post(`/portal/startups/${sid}/submit`);
      toast.success("Submitted! LaunchLab AI is now analyzing your startup.");
      navigate(`/dashboard/startup/${sid}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reviewRows = useMemo(
    () => Object.entries(answers).filter(([k, v]) => k !== "_language" && v && (!Array.isArray(v) || v.length)),
    [answers]
  );

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>;
  }

  return (
    <PortalShell user={user} title="New Startup Project">
      <div className="max-w-3xl mx-auto" data-testid="startup-wizard">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2.5">
            <p className="font-mono-tech text-[11px] tracking-[0.3em] text-[#00B0FF] uppercase" data-testid="wizard-progress-label">
              Step {step + 1} of {STEPS.length} — {(lang !== "en" && STEP_I18N[current.id]?.[lang]) || current.title}
            </p>
            <div className="flex items-center gap-3">
              <Select value={lang} onValueChange={setLang}>
                <SelectTrigger data-testid="wizard-language" className="bg-[#06080E] border-white/10 text-slate-200 h-8 w-[128px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200">
                  {LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code} data-testid={`wizard-lang-${l.code}`}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <button onClick={() => save()} disabled={saving} data-testid="wizard-save-later" className="text-xs text-slate-400 hover:text-[#FF8800] transition-colors flex items-center gap-1.5">
                <Save className="w-3.5 h-3.5" /> {saving ? "Saving..." : "Save & Continue Later"}
              </button>
            </div>
          </div>
          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-[#FF8800] to-[#00E676] transition-all duration-500" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} data-testid="wizard-progress-bar" />
          </div>
        </div>

        <motion.div
          key={current.id}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          className="glass rounded-2xl p-6 sm:p-9"
        >
          {step === 0 && (
            <div className="space-y-5 mb-6">
              <div className="space-y-2">
                <Label className="text-slate-300">Project Name *</Label>
                <Input data-testid="wizard-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Organic Millet Snacks" className="bg-[#06080E] border-white/10" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Brand Name (if decided)</Label>
                <Input data-testid="wizard-brand" value={brandName} onChange={(e) => setBrandName(e.target.value)} className="bg-[#06080E] border-white/10" />
              </div>
            </div>
          )}

          {current.info && (
            <div className="rounded-xl border border-[#00B0FF]/30 bg-[#00B0FF]/[0.05] px-5 py-4 text-sm text-slate-300 leading-relaxed mb-2" data-testid="wizard-info">
              {current.info}
            </div>
          )}

          {current.id === "review" ? (
            <div data-testid="wizard-review">
              <p className="text-sm text-slate-400 mb-5">Review your answers before AI analysis. You can go back to edit any step.</p>
              <div className="space-y-2.5 max-h-[46vh] overflow-y-auto pr-1">
                <div className="glass rounded-lg px-4 py-3">
                  <p className="text-[10px] font-mono-tech tracking-widest text-slate-500 uppercase">Project</p>
                  <p className="text-sm text-white mt-0.5">{name}{brandName ? ` · ${brandName}` : ""}</p>
                </div>
                {reviewRows.map(([k, v]) => (
                  <div key={k} className="glass rounded-lg px-4 py-3">
                    <p className="text-[10px] font-mono-tech tracking-widest text-slate-500 uppercase">{k.replace(/_/g, " ")}</p>
                    <p className="text-sm text-slate-200 mt-0.5 whitespace-pre-wrap">{Array.isArray(v) ? v.join(", ") : v}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {current.fields.map((f) => {
                const tr = lang !== "en" ? FIELD_I18N[f.key]?.[lang] : null;
                const ex = FIELD_EXAMPLES[f.key]?.[lang] || FIELD_EXAMPLES[f.key]?.en;
                return (
                  <div key={f.key} className="space-y-2">
                    <Label className="text-slate-300">
                      {tr ? (
                        <>
                          {tr}{f.required ? " *" : ""}
                          <span className="block text-[11px] font-normal text-slate-500 mt-0.5">{f.label}</span>
                        </>
                      ) : (
                        <>{f.label}{f.required ? " *" : ""}</>
                      )}
                    </Label>
                    {ex && <p className="text-[11px] text-slate-500 italic leading-relaxed" data-testid={`wizard-example-${f.key}`}>{ex}</p>}
                    <StepField field={f} value={answers[f.key]} lang={lang} onChange={(v) => setAnswers((a) => ({ ...a, [f.key]: v }))} />
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>

        <div className="flex items-center justify-between mt-7">
          <Button
            variant="outline"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            data-testid="wizard-prev"
            className="border-white/15 text-slate-300 hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          {current.id === "review" ? (
            <Button onClick={submit} disabled={submitting} data-testid="wizard-submit" className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold px-7">
              <Sparkles className="w-4 h-4 mr-2" />
              {submitting ? "Submitting..." : "Submit & Validate My Startup"}
            </Button>
          ) : (
            <Button onClick={next} data-testid="wizard-next" className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold px-7">
              Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </PortalShell>
  );
}
